import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { useAppShell } from '../contexts/AppShellContext';
import { getMaterialReceipts } from '../services/materialReceiptService';
import type { MaterialReceiptRow } from '../types/api';
import { CATEGORY_FILTERS } from '../utils/jobCategory';
import { MaterialReceiptsTable } from '../components/materialReceiptsShared';
import { RegulatoryAllReceiptsTable } from '../components/regulatory/RegulatoryAllReceiptsTable';

const MATERIAL_TABS = [
  { id: 'proxy',      label: 'Proxy, Corp Action & Issuer' },
  { id: 'regulatory', label: 'Regulatory' },
];

// Regulatory has its own tab now, so it doesn't need its own pill here too.
const PROXY_CATEGORY_FILTERS = CATEGORY_FILTERS.filter(c => c.id !== 'R');

const COLLAPSE_LS_KEY = 'opsai.allReceipts.collapsedGroups.v1';

export function MaterialReceiptsPage() {
  const { setTopbar } = useAppShell();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = (useOutletContext() as any) || {};

  const [rows, setRows]       = useState<MaterialReceiptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [materialTab, setMaterialTab] = useState<'proxy' | 'regulatory'>('proxy');

  useEffect(() => { setTopbar('All Receipts', 'Every material receipt · click a row to open in Ace'); }, [setTopbar]);

  useEffect(() => {
    let alive = true;
    const load = () => getMaterialReceipts()
      .then(r => { if (alive) setRows(r); })
      .catch(e => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    load();
    const iv = setInterval(load, 3000);  // live: reflect new receipts + status changes
    return () => { alive = false; clearInterval(iv); };
  }, []);

  // Regulatory receipts live entirely in their own tab — this mirrors the
  // Intake Queue's proxyRows/regulatoryRows split (IntakeQueuePage.tsx).
  const proxyRows = useMemo(() => rows.filter(r => r.job_category !== 'R'), [rows]);
  const regulatoryRows = useMemo(() => rows.filter(r => r.job_category === 'R'), [rows]);

  const onSaved = useCallback((u: MaterialReceiptRow) => setRows(prev => prev.map(r => r.id === u.id ? u : r)), []);
  const openAce = useCallback((r: MaterialReceiptRow) => navigate('/ace', { state: { receiptThread: { receiptId: r.id, scan: r.scan_number, client: r.client_names.join(', '), row: r } } }), [navigate]);

  const focusReceiptId = (location.state as any)?.focusReceiptId ?? null;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Material-type tabs — styled like the Receiving Log tabs. */}
      <div className="flex gap-0 px-6 border-b border-trim flex-shrink-0">
        {MATERIAL_TABS.map(t => (
          <button key={t.id} onClick={() => setMaterialTab(t.id as 'proxy' | 'regulatory')}
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap"
            style={{ borderColor: materialTab === t.id ? '#2D7575' : 'transparent', color: materialTab === t.id ? '#256060' : '#73747A' }}>
            {t.label}
          </button>
        ))}
      </div>

      {error ? (
        <div className="px-6 py-5 text-sm text-red-600">{error}</div>
      ) : materialTab === 'regulatory' ? (
        <RegulatoryAllReceiptsTable rows={regulatoryRows} onSaved={onSaved} openAce={openAce} showToast={showToast} loading={loading} />
      ) : (
        <MaterialReceiptsTable
          rows={proxyRows}
          categoryFilters={PROXY_CATEGORY_FILTERS}
          onSaved={onSaved}
          openAce={openAce}
          showToast={showToast}
          collapseStorageKey={COLLAPSE_LS_KEY}
          emptyMessage="No receipts match the current filter."
          loading={loading}
          initialViewReceiptId={focusReceiptId}
        />
      )}
    </div>
  );
}
